import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "../services/authService";
import { adminAuthService } from "../services/adminAuthService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Restore regular user session
      const storedUser = authService.getCurrentUser();
      if (storedUser && authService.isAuthenticated()) {
        setUser(storedUser);
        setIsAuthenticated(true);
        if (storedUser.must_change_password) setPasswordChangeRequired(true);
      }

      // Restore admin session and verify token
      const storedAdmin = adminAuthService.getCurrentAdminUser();
      if (storedAdmin && adminAuthService.isAdminAuthenticated()) {
        setAdminUser(storedAdmin);
        setIsAdminAuthenticated(true);
        try {
          const verified = await adminAuthService.verifyAdminToken();
          setAdminUser(verified);
          setIsAdminAuthenticated(true);
        } catch {
          adminAuthService.clearAdminSession();
          setAdminUser(null);
          setIsAdminAuthenticated(false);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Regular user auth
  const login = async (username, password) => {
    const { user: userData, passwordChangeRequired: mustChange } = await authService.login(username, password);
    setUser(userData);
    setIsAuthenticated(true);
    setPasswordChangeRequired(mustChange);
    return { user: userData, passwordChangeRequired: mustChange };
  };

  const clearPasswordChangeRequired = () => setPasswordChangeRequired(false);

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setPasswordChangeRequired(false);
  };

  // Admin auth
  const adminLogin = async (email, password) => {
    const result = await adminAuthService.adminLogin(email, password);
    setAdminUser(result.adminUser);
    setIsAdminAuthenticated(true);
    return result;
  };

  const adminLogout = async () => {
    try { await adminAuthService.adminLogout(); } catch {}
    adminAuthService.clearAdminSession();
    setAdminUser(null);
    setIsAdminAuthenticated(false);
  };

  const isAdmin = () => user?.role === "admin";

  const companyname = process.env.REACT_APP_CLIENT_NAME || "iWMS";
  const companylogo = process.env.REACT_APP_CLIENT_LOGO || "/Assets/images/iWMS.png";

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        passwordChangeRequired,
        clearPasswordChangeRequired,
        loading,
        login,
        logout,
        isAdmin,
        companyname,
        companylogo,
        // Admin
        adminUser,
        isAdminAuthenticated,
        adminLogin,
        adminLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
